import React, { useCallback, useEffect, useState } from "react";
import {
  Accordion, Form, Modal, Spinner,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import bmsService, {
  BmsUser, BmsAvailability, BmsUserService, BmsServiceItem,
} from "../../features/bms/bmsService";
import {
  TbUsersGroup, TbPlus, TbTrash, TbCalendar, TbBriefcase, TbUserPlus, TbX, TbCheck, TbEdit,
  TbClock, TbCoffee,
} from "react-icons/tb";
import BmsChatbotSelector from "../../components/bms/BmsChatbotSelector";
import BmsPagination from "../../components/bms/BmsPagination";
import BmsTimePicker from "../../components/bms/BmsTimePicker";
import { showSuccess, showError, showConfirm } from "../../utils/bmsToast";

const PAGE_SIZE = 25;

const DAY_KEYS = [1, 2, 3, 4, 5, 6, 0] as const;
const WEEKDAY_KEYS = [1, 2, 3, 4, 5];
const WEEKEND_KEYS = [6, 0];

const BmsUsersPage: React.FC = () => {
  const { t } = useTranslation();
  const [chatbotId, setChatbotId] = useState<number | null>(null);
  const [users, setUsers] = useState<BmsUser[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [isLoading, setIsLoading] = useState(false);

  const [availableUsers, setAvailableUsers] = useState<Array<{ company_user_id: number; full_name: string; email: string }>>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedCompanyUserId, setSelectedCompanyUserId] = useState<number | null>(null);

  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
  const [userAvailability, setUserAvailability] = useState<BmsAvailability[]>([]);
  const [userServices, setUserServices] = useState<BmsUserService[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [allServices, setAllServices] = useState<BmsServiceItem[]>([]);

  const [showAvailForm, setShowAvailForm] = useState(false);
  const [editingAvailId, setEditingAvailId] = useState<number | null>(null);
  const [availForm, setAvailForm] = useState({ start_at: "09:00", end_at: "18:00", break_start_at: "", break_end_at: "", days: [1, 2, 3, 4, 5] as number[] });
  const [savingAvail, setSavingAvail] = useState(false);

  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState<number | null>(null);
  const [serviceForm, setServiceForm] = useState({ service_id: 0, duration_override: "", is_active: true });
  const [savingService, setSavingService] = useState(false);

  const loadUsers = useCallback(async () => {
    if (!chatbotId) { setUsers([]); setUsersTotal(0); return; }
    setIsLoading(true);
    try {
      const res = await bmsService.listUsers(chatbotId, { limit, skip });
      setUsers(res.data || []);
      setUsersTotal(res.total ?? 0);
    } catch (e: any) {
      showError(e?.response?.data?.detail || e?.message || "Failed to load BMS users");
    } finally {
      setIsLoading(false);
    }
  }, [chatbotId, limit, skip]);

  useEffect(() => { loadUsers(); setExpandedUserId(null); }, [loadUsers]);
  useEffect(() => { setSkip(0); }, [chatbotId]);

  const toggleExpand = async (userId: number) => {
    if (expandedUserId === userId) { setExpandedUserId(null); return; }
    setExpandedUserId(userId);
    if (!chatbotId) return;
    setLoadingDetail(true);
    try {
      const [availRes, svcRes, allSvcRes] = await Promise.all([
        bmsService.getUserAvailability(chatbotId, userId),
        bmsService.getUserServices(chatbotId, userId),
        bmsService.listServices(chatbotId, { limit: 200 }),
      ]);
      setUserAvailability(availRes.data || []);
      setUserServices(svcRes.data || []);
      setAllServices((allSvcRes.data || []).filter((s) => s.is_active));
    } catch (e: any) {
      showError(e?.response?.data?.detail || e?.message || "Error");
    } finally {
      setLoadingDetail(false);
    }
  };

  const refreshDetail = async (userId: number) => {
    if (!chatbotId) return;
    try {
      const [availRes, svcRes] = await Promise.all([
        bmsService.getUserAvailability(chatbotId, userId),
        bmsService.getUserServices(chatbotId, userId),
      ]);
      setUserAvailability(availRes.data || []);
      setUserServices(svcRes.data || []);
    } catch { /* silent */ }
  };

  const openAddModal = async () => {
    if (!chatbotId) return;
    try {
      const res = await bmsService.listAvailableCompanyUsers(chatbotId);
      setAvailableUsers(res.data || []);
      setSelectedCompanyUserId(res.data?.[0]?.company_user_id ?? null);
      setShowAddModal(true);
    } catch (e: any) {
      showError(e?.response?.data?.detail || e?.message || "Error");
    }
  };

  const addUser = async () => {
    if (!chatbotId || !selectedCompanyUserId) return;
    try {
      await bmsService.addUser(chatbotId, selectedCompanyUserId);
      setShowAddModal(false);
      showSuccess(t("bms_add_expert"));
      await loadUsers();
    } catch (e: any) {
      showError(e?.response?.data?.detail || e?.message || "Error");
    }
  };

  const handleDeactivateUser = async (userId: number) => {
    if (!chatbotId) return;
    const confirmed = await showConfirm({
      title: t("bms_confirm_title"),
      text: t("bms_confirm_deactivate_user"),
      confirmText: t("bms_deactivate_user"),
      cancelText: t("bms_cancel"),
    });
    if (!confirmed) return;
    try {
      await bmsService.deactivateUser(chatbotId, userId);
      showSuccess(t("bms_user_deactivated"));
      if (expandedUserId === userId) setExpandedUserId(null);
      await loadUsers();
    } catch (e: any) {
      showError(e?.response?.data?.detail || e?.message || "Error");
    }
  };

  const openAvailCreate = () => {
    setEditingAvailId(null);
    setAvailForm({ start_at: "09:00", end_at: "18:00", break_start_at: "", break_end_at: "", days: [1, 2, 3, 4, 5] });
    setShowAvailForm(true);
  };

  const openAvailEdit = (a: BmsAvailability) => {
    let days: number[] = [];
    try { days = JSON.parse(a.days); } catch { /* empty */ }
    setEditingAvailId(a.id);
    setAvailForm({
      start_at: a.start_at || "09:00",
      end_at: a.end_at || "18:00",
      break_start_at: a.break_start_at || "",
      break_end_at: a.break_end_at || "",
      days,
    });
    setShowAvailForm(true);
  };

  const handleSaveAvailability = async () => {
    if (!chatbotId || !expandedUserId || availForm.days.length === 0) return;
    setSavingAvail(true);
    try {
      if (editingAvailId) {
        await bmsService.updateAvailability(chatbotId, editingAvailId, {
          start_at: availForm.start_at, end_at: availForm.end_at,
          break_start_at: availForm.break_start_at || "",
          break_end_at: availForm.break_end_at || "",
          days: availForm.days,
        });
        showSuccess(t("bms_availability_updated"));
      } else {
        await bmsService.createAvailability(chatbotId, {
          user_id: expandedUserId,
          start_at: availForm.start_at, end_at: availForm.end_at,
          break_start_at: availForm.break_start_at || undefined,
          break_end_at: availForm.break_end_at || undefined,
          days: availForm.days,
        });
        showSuccess(t("bms_availability_created"));
      }
      setShowAvailForm(false);
      setEditingAvailId(null);
      setAvailForm({ start_at: "09:00", end_at: "18:00", break_start_at: "", break_end_at: "", days: [1, 2, 3, 4, 5] });
      await refreshDetail(expandedUserId);
    } catch (e: any) {
      showError(e?.response?.data?.detail || e?.message || "Error");
    } finally {
      setSavingAvail(false);
    }
  };

  const handleDeleteAvailability = async (availId: number) => {
    if (!chatbotId || !expandedUserId) return;
    const confirmed = await showConfirm({
      title: t("bms_confirm_title"),
      text: t("bms_confirm_delete_availability"),
      confirmText: t("bms_confirm_yes"),
      cancelText: t("bms_confirm_no"),
    });
    if (!confirmed) return;
    try {
      await bmsService.deleteAvailability(chatbotId, availId);
      showSuccess(t("bms_availability_deleted"));
      await refreshDetail(expandedUserId);
    } catch (e: any) {
      showError(e?.response?.data?.detail || e?.message || "Error");
    }
  };

  const openServiceForm = () => {
    const assignedIds = new Set(userServices.map((us) => us.service_id));
    const available = allServices.filter((s) => !assignedIds.has(s.id));
    if (available.length === 0) { showError(t("bms_no_services_available")); return; }
    setEditingServiceId(null);
    setServiceForm({ service_id: available[0].id, duration_override: "", is_active: true });
    setShowServiceForm(true);
  };

  const openServiceEdit = (us: BmsUserService) => {
    setEditingServiceId(us.id);
    setServiceForm({
      service_id: us.service_id,
      duration_override: us.duration_override != null ? String(us.duration_override) : "",
      is_active: !!us.is_active,
    });
    setShowServiceForm(true);
  };

  const handleAssignService = async () => {
    if (!chatbotId || !expandedUserId) return;
    setSavingService(true);
    try {
      if (editingServiceId) {
        const hasOverride = serviceForm.duration_override !== "" && Number(serviceForm.duration_override) > 0;
        await bmsService.updateUserService(chatbotId, editingServiceId, {
          duration_override: hasOverride ? Number(serviceForm.duration_override) : undefined,
          clear_duration_override: !hasOverride,
          is_active: serviceForm.is_active,
        });
        showSuccess(t("bms_service_updated"));
      } else {
        if (!serviceForm.service_id) return;
        await bmsService.assignUserService(chatbotId, {
          user_id: expandedUserId,
          service_id: serviceForm.service_id,
          duration_override: serviceForm.duration_override ? Number(serviceForm.duration_override) : undefined,
          is_active: serviceForm.is_active,
        });
        showSuccess(t("bms_service_assigned"));
      }
      setShowServiceForm(false);
      setEditingServiceId(null);
      await refreshDetail(expandedUserId);
    } catch (e: any) {
      showError(e?.response?.data?.detail || e?.message || "Error");
    } finally {
      setSavingService(false);
    }
  };

  const handleUnassignService = async (userServiceId: number) => {
    if (!chatbotId || !expandedUserId) return;
    const confirmed = await showConfirm({
      title: t("bms_confirm_title"),
      text: t("bms_confirm_unassign_service"),
      confirmText: t("bms_confirm_yes"),
      cancelText: t("bms_confirm_no"),
    });
    if (!confirmed) return;
    try {
      await bmsService.removeUserService(chatbotId, userServiceId);
      showSuccess(t("bms_service_unassigned"));
      await refreshDetail(expandedUserId);
    } catch (e: any) {
      showError(e?.response?.data?.detail || e?.message || "Error");
    }
  };

  const toggleDay = (day: number) => {
    setAvailForm((prev) => ({
      ...prev,
      days: prev.days.includes(day) ? prev.days.filter((d) => d !== day) : [...prev.days, day].sort(),
    }));
  };

  const setPresetDays = (preset: "weekdays" | "weekend" | "all" | "clear") => {
    const map: Record<string, number[]> = {
      weekdays: [...WEEKDAY_KEYS],
      weekend: [...WEEKEND_KEYS],
      all: [0, 1, 2, 3, 4, 5, 6],
      clear: [],
    };
    setAvailForm((prev) => ({ ...prev, days: map[preset] }));
  };

  const parseDays = (raw: string): number[] => {
    try { return JSON.parse(raw); } catch { return []; }
  };

  const assignableServices = allServices.filter((s) => !userServices.some((us) => us.service_id === s.id));

  return (
    <div className="chatbots-page bms-page">
      <div className="page-header-card mb-3">
        <div className="page-header-text">
          <h1 className="d-flex align-items-center gap-2"><TbUsersGroup /> {t("bms_users")}</h1>
          <p>{t("bms_users_desc")}</p>
        </div>
      </div>

      <div className="p-3 pt-0">
        <BmsChatbotSelector chatbotId={chatbotId} onChatbotChange={setChatbotId} />

        <div className="bms-toolbar bms-users-toolbar mt-3">
          <div className="d-flex align-items-center gap-2 bms-users-toolbar-meta">
            <span className="fw-semibold" style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>
              {usersTotal} {t("bms_users").toLowerCase()}
            </span>
          </div>
          <button className="bms-btn-primary bms-users-toolbar-add-btn" onClick={openAddModal} disabled={!chatbotId}>
            <TbUserPlus /> {t("bms_add_expert")}
          </button>
        </div>

        {isLoading ? (
          <div className="d-flex justify-content-center py-5"><Spinner animation="border" variant="primary" /></div>
        ) : users.length === 0 ? (
          <div className="bms-section-card">
            <div className="bms-empty">
              <div className="bms-empty-icon"><TbUsersGroup /></div>
              <p>{t("bms_no_data")}</p>
            </div>
          </div>
        ) : (
          <>
          <Accordion activeKey={expandedUserId?.toString()} className="bms-expert-accordion">
            {users.map((u) => (
              <Accordion.Item key={u.id} eventKey={u.id.toString()}>
                <Accordion.Header onClick={() => toggleExpand(u.id)}>
                  <div className="bms-expert-header-content w-100 me-3">
                    <div className="bms-expert-header-main">
                      <div style={{
                        width: 36, height: 36, borderRadius: 10,
                        background: "var(--gradient-primary)", color: "white",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontWeight: 700, fontSize: "0.85rem", flexShrink: 0,
                      }}>
                        {u.display_name?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                      <div className="bms-expert-header-text">
                        <div className="fw-semibold" style={{ fontSize: "0.95rem" }}>{u.display_name}</div>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>ID: {u.id}</div>
                      </div>
                    </div>
                    <div className="bms-expert-header-actions">
                      <span className={`bms-status ${u.status}`}>{u.status}</span>
                      {u.status === "active" && (
                        <button
                          className="bms-action-btn danger"
                          onClick={(e) => { e.stopPropagation(); handleDeactivateUser(u.id); }}
                          title={t("bms_deactivate_user")}
                        >
                          <TbTrash />
                        </button>
                      )}
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  {loadingDetail && expandedUserId === u.id ? (
                    <div className="d-flex justify-content-center py-4"><Spinner animation="border" size="sm" variant="primary" /></div>
                  ) : expandedUserId === u.id ? (
                    <div className="d-flex flex-column gap-4">
                      {/* Availability */}
                      <div className="bms-section-card">
                        <div className="bms-section-header bms-users-section-header">
                          <div className="bms-section-title">
                            <span className="bms-section-icon"><TbCalendar /></span>
                            {t("bms_availability")}
                          </div>
                          <button className="bms-btn-primary bms-users-section-btn" onClick={openAvailCreate} style={{ fontSize: "0.8rem", padding: "6px 14px" }}>
                            <TbPlus /> {t("bms_add_availability")}
                          </button>
                        </div>
                        {userAvailability.length === 0 ? (
                          <div className="bms-empty" style={{ padding: "1.5rem" }}>
                            <p>{t("bms_no_data")}</p>
                          </div>
                        ) : (
                          <div style={{ overflowX: "auto" }}>
                            <table className="bms-table bms-users-table table mb-0">
                              <thead>
                                <tr>
                                  <th>{t("bms_availability_days")}</th>
                                  <th>{t("bms_availability_start")}</th>
                                  <th>{t("bms_availability_end")}</th>
                                  <th>{t("bms_availability_break_start")}</th>
                                  <th>{t("bms_availability_break_end")}</th>
                                  <th style={{ width: 50 }}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {userAvailability.map((a) => (
                                  <tr key={a.id}>
                                    <td data-label={t("bms_availability_days")}>
                                      <div className="d-flex gap-1 flex-wrap">
                                        {parseDays(a.days).map((d) => (
                                          <span key={d} className="bms-day-badge">{t(`bms_day_${d}`)}</span>
                                        ))}
                                      </div>
                                    </td>
                                    <td className="fw-medium" data-label={t("bms_availability_start")}>{a.start_at}</td>
                                    <td className="fw-medium" data-label={t("bms_availability_end")}>{a.end_at}</td>
                                    <td data-label={t("bms_availability_break_start")}>{a.break_start_at || "—"}</td>
                                    <td data-label={t("bms_availability_break_end")}>{a.break_end_at || "—"}</td>
                                    <td data-label={t("actions")}>
                                      <div className="d-flex gap-1">
                                        <button className="bms-action-btn edit" onClick={() => openAvailEdit(a)} title={t("edit")}><TbEdit /></button>
                                        <button className="bms-action-btn danger" onClick={() => handleDeleteAvailability(a.id)} title={t("delete")}><TbTrash /></button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>

                      {/* Services */}
                      <div className="bms-section-card">
                        <div className="bms-section-header bms-users-section-header">
                          <div className="bms-section-title">
                            <span className="bms-section-icon"><TbBriefcase /></span>
                            {t("bms_expert_services")}
                          </div>
                          <button className="bms-btn-primary bms-users-section-btn" onClick={openServiceForm} style={{ fontSize: "0.8rem", padding: "6px 14px" }}>
                            <TbPlus /> {t("bms_assign_service")}
                          </button>
                        </div>
                        {userServices.length === 0 ? (
                          <div className="bms-empty" style={{ padding: "1.5rem" }}>
                            <p>{t("bms_no_data")}</p>
                          </div>
                        ) : (
                          <div style={{ overflowX: "auto" }}>
                            <table className="bms-table bms-users-table table mb-0">
                              <thead>
                                <tr>
                                  <th>{t("bms_service_name")}</th>
                                  <th>{t("bms_duration_override")}</th>
                                  <th>{t("bms_status")}</th>
                                  <th style={{ width: 50 }}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {userServices.map((us) => (
                                  <tr key={us.id}>
                                    <td className="fw-semibold" data-label={t("bms_service_name")}>{us.service_name || `Service #${us.service_id}`}</td>
                                    <td data-label={t("bms_duration_override")}>{us.duration_override ? `${us.duration_override} ${t("bms_minutes_short")}` : "—"}</td>
                                    <td data-label={t("bms_status")}>
                                      <span className={`bms-status ${us.is_active ? "active" : "inactive"}`}>
                                        {us.is_active ? t("active") : t("inactive")}
                                      </span>
                                    </td>
                                    <td data-label={t("actions")}>
                                      <div className="d-flex gap-1 justify-content-end">
                                        <button className="bms-action-btn edit" onClick={() => openServiceEdit(us)} title={t("edit")}><TbEdit /></button>
                                        <button className="bms-action-btn danger" onClick={() => handleUnassignService(us.id)} title={t("delete")}><TbTrash /></button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
          <BmsPagination total={usersTotal} skip={skip} limit={limit} onPageChange={setSkip} onLimitChange={(v) => { setLimit(v); setSkip(0); }} />
          </>
        )}
      </div>

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} className="bms-modal" centered>
        <Modal.Header closeButton className="bms-modal-header">
          <Modal.Title className="d-flex align-items-center gap-2"><TbUserPlus /> {t("bms_add_expert_from_company")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {availableUsers.length === 0 ? (
            <div className="text-center text-muted py-3">{t("bms_no_data")}</div>
          ) : (
            <Form.Select value={selectedCompanyUserId ?? ""} onChange={(e) => setSelectedCompanyUserId(Number(e.target.value))}>
              {availableUsers.map((u) => (
                <option key={u.company_user_id} value={u.company_user_id}>
                  {u.full_name} ({u.email})
                </option>
              ))}
            </Form.Select>
          )}
        </Modal.Body>
        <Modal.Footer className="bms-modal-footer">
          <button className="bms-btn-secondary" onClick={() => setShowAddModal(false)}><TbX /> {t("cancel")}</button>
          <button className="bms-btn-primary" onClick={addUser} disabled={!selectedCompanyUserId || availableUsers.length === 0}>
            <TbCheck /> {t("bms_add_expert")}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAvailForm} onHide={() => { setShowAvailForm(false); setEditingAvailId(null); }} className="bms-modal" centered size="lg">
        <Modal.Header closeButton className="bms-modal-header">
          <Modal.Title className="d-flex align-items-center gap-2">
            <TbCalendar /> {editingAvailId ? t("bms_edit_availability") : t("bms_add_availability")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {/* Day Selection */}
          <div className="bms-avail-section">
            <div className="bms-avail-section-label">
              <TbCalendar className="bms-avail-section-icon" />
              <div>
                <div className="fw-semibold">{t("bms_availability_days")}</div>
                <div className="text-muted" style={{ fontSize: "0.8rem" }}>{t("bms_select_days")}</div>
              </div>
            </div>

            <div className="bms-avail-presets">
              <button type="button" className="bms-preset-btn" onClick={() => setPresetDays("weekdays")}>{t("bms_weekdays")}</button>
              <button type="button" className="bms-preset-btn" onClick={() => setPresetDays("weekend")}>{t("bms_weekend")}</button>
              <button type="button" className="bms-preset-btn" onClick={() => setPresetDays("all")}>{t("bms_all_days")}</button>
              <button type="button" className="bms-preset-btn outline" onClick={() => setPresetDays("clear")}>{t("bms_clear_days")}</button>
            </div>

            <div className="bms-avail-days-grid">
              {DAY_KEYS.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`bms-avail-day-btn ${availForm.days.includes(d) ? "selected" : ""}`}
                  onClick={() => toggleDay(d)}
                >
                  <span className="bms-avail-day-short">{t(`bms_day_${d}`)}</span>
                  <span className="bms-avail-day-full">{t(`bms_day_full_${d}`)}</span>
                </button>
              ))}
            </div>
            {availForm.days.length === 0 && (
              <div className="text-danger mt-2" style={{ fontSize: "0.8rem" }}>{t("bms_no_days_selected")}</div>
            )}
          </div>

          {/* Working Hours */}
          <div className="bms-avail-section">
            <div className="bms-avail-section-label">
              <TbClock className="bms-avail-section-icon" />
              <div className="fw-semibold">{t("bms_working_hours")}</div>
            </div>
            <div className="bms-avail-time-row">
              <div className="bms-avail-time-field">
                <label>{t("bms_availability_start")}</label>
                <BmsTimePicker
                  value={availForm.start_at}
                  onChange={(v) => setAvailForm((p) => ({ ...p, start_at: v }))}
                />
              </div>
              <div className="bms-avail-time-separator">—</div>
              <div className="bms-avail-time-field">
                <label>{t("bms_availability_end")}</label>
                <BmsTimePicker
                  value={availForm.end_at}
                  onChange={(v) => setAvailForm((p) => ({ ...p, end_at: v }))}
                />
              </div>
            </div>
          </div>

          {/* Break Time */}
          <div className="bms-avail-section" style={{ borderBottom: "none" }}>
            <div className="bms-avail-section-label">
              <TbCoffee className="bms-avail-section-icon" />
              <div>
                <div className="fw-semibold">{t("bms_break_time")}</div>
                <div className="text-muted" style={{ fontSize: "0.8rem" }}>{t("bms_break_optional")}</div>
              </div>
            </div>
            <div className="bms-avail-time-row">
              <div className="bms-avail-time-field">
                <label>{t("bms_availability_break_start")}</label>
                <BmsTimePicker
                  value={availForm.break_start_at}
                  onChange={(v) => setAvailForm((p) => ({ ...p, break_start_at: v }))}
                  allowClear
                />
              </div>
              <div className="bms-avail-time-separator">—</div>
              <div className="bms-avail-time-field">
                <label>{t("bms_availability_break_end")}</label>
                <BmsTimePicker
                  value={availForm.break_end_at}
                  onChange={(v) => setAvailForm((p) => ({ ...p, break_end_at: v }))}
                  allowClear
                />
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="bms-modal-footer">
          <button className="bms-btn-secondary" onClick={() => { setShowAvailForm(false); setEditingAvailId(null); }}><TbX /> {t("cancel")}</button>
          <button className="bms-btn-primary" onClick={handleSaveAvailability} disabled={savingAvail || availForm.days.length === 0}>
            {savingAvail ? <Spinner size="sm" animation="border" /> : <TbCheck />} {t("save")}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={showServiceForm} onHide={() => { setShowServiceForm(false); setEditingServiceId(null); }} className="bms-modal" centered>
        <Modal.Header closeButton className="bms-modal-header">
          <Modal.Title className="d-flex align-items-center gap-2">
            <TbBriefcase /> {editingServiceId ? t("bms_edit_service") : t("bms_assign_service")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!editingServiceId && assignableServices.length === 0 ? (
            <div className="text-center text-muted py-3">{t("bms_no_services_available")}</div>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">{t("bms_select_service")} *</Form.Label>
                {editingServiceId ? (
                  <Form.Control
                    type="text"
                    disabled
                    value={(() => {
                      const s = allServices.find((x) => x.id === serviceForm.service_id);
                      return s ? `${s.name} (${s.duration_minutes} ${t("bms_minutes_short")})` : "";
                    })()}
                  />
                ) : (
                  <Form.Select value={serviceForm.service_id} onChange={(e) => setServiceForm((p) => ({ ...p, service_id: Number(e.target.value) }))}>
                    {assignableServices.map((s) => (
                      <option key={s.id} value={s.id}>{s.name} ({s.duration_minutes} {t("bms_minutes_short")})</option>
                    ))}
                  </Form.Select>
                )}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">{t("bms_duration_override")}</Form.Label>
                <Form.Control
                  type="number"
                  min={5}
                  max={480}
                  placeholder={(() => {
                    const s = allServices.find((x) => x.id === serviceForm.service_id);
                    return s ? `${t("bms_default")}: ${s.duration_minutes} ${t("bms_minutes_short")}` : t("bms_duration_override");
                  })()}
                  value={serviceForm.duration_override}
                  onChange={(e) => setServiceForm((p) => ({ ...p, duration_override: e.target.value }))}
                />
                <Form.Text className="text-muted">{t("bms_duration_override_hint")}</Form.Text>
              </Form.Group>
              <Form.Group>
                <Form.Check
                  type="switch"
                  id="bms-user-service-active-switch"
                  label={t("active")}
                  checked={serviceForm.is_active}
                  onChange={(e) => setServiceForm((p) => ({ ...p, is_active: e.target.checked }))}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="bms-modal-footer">
          <button className="bms-btn-secondary" onClick={() => { setShowServiceForm(false); setEditingServiceId(null); }}><TbX /> {t("cancel")}</button>
          <button
            className="bms-btn-primary"
            onClick={handleAssignService}
            disabled={savingService || (!editingServiceId && assignableServices.length === 0)}
          >
            {savingService ? <Spinner size="sm" animation="border" /> : <TbCheck />} {editingServiceId ? t("save") : t("bms_assign_service")}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BmsUsersPage;
